/**
 * TopTractsSidePanel.tsx — desktop slide-out for the ranked "Top tracts in view" list
 * (T-222 / BEAD-0040 R2).
 *
 * Ricardo's revision: the always-docked right-rail panel is gone; a compact CTA
 * (TopTractsCta) opens THIS surface, which slides out of the right edge using the SAME
 * idiom/animation/anchor as the tract-detail panel (InfoSidePanel) — fixed right edge below
 * the TopBar, translate-x transition, Escape to close, a titled header with a close button.
 * The body is the shared TopTractsList (sort control + ranked rows); the ranking core and all
 * six sort modes are untouched.
 *
 * Coexistence with the tract detail: clicking a row drives the shared selection path
 * (onSelectTract) which closes this surface and reveals the tract detail — one right-edge
 * surface at a time, matching how a map click and the ScorePanel → InfoSidePanel drill-down
 * already behave. The parent (MapShell) owns that transition.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TopTractsList } from './TopTractsList'
import type { CensusTract } from '../../lib/tractModel'
import type { MapTier } from '../../lib/mapTiers'

export interface TopTractsSidePanelProps {
  open: boolean
  onClose: () => void
  tier: MapTier
  visible: boolean
  tierFilter?: string[]
  typeFilter?: string[]
  onSelectTract: (tract: CensusTract, centroid: [number, number]) => void
  limit?: number
}

export function TopTractsSidePanel({
  open,
  onClose,
  tier,
  visible,
  tierFilter,
  typeFilter,
  onSelectTract,
  limit,
}: TopTractsSidePanelProps) {
  // Escape closes the panel — same as InfoSidePanel.
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <div
      role="complementary"
      aria-label="Top tracts in view"
      aria-hidden={!open}
      className={cn(
        'fixed top-12 right-0 bottom-0 z-20 w-[420px]',
        'bg-card border-l border-border shadow-xl',
        'flex flex-col',
        'transition-transform duration-200 ease-in-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      {/* ── Header — teal top stripe signals brand identity (matches InfoSidePanel) ── */}
      <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border border-t-2 border-t-primary">
        <span className="text-sm font-bold text-foreground truncate">Top tracts in view</span>
        <button
          onClick={onClose}
          aria-label="Close top tracts panel"
          className="ml-2 flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground
                     hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-ring min-h-[36px] min-w-[36px] flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Body — the shared ranked list owns the single scroll surface ─────────── */}
      <div className="oy-scroll flex-1 overflow-y-auto">
        <TopTractsList
          tier={tier}
          visible={visible}
          tierFilter={tierFilter}
          typeFilter={typeFilter}
          onSelectTract={onSelectTract}
          limit={limit}
        />
      </div>
    </div>
  )
}
